import { SavedSong } from '../domains/games/services/GameAPI';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../shared/storage/firebase';

interface SongCreationEmailPayload {
  parentEmail: string;
  studentName: string;
  song: SavedSong;
  userStats: {
    totalSongs: number;
    totalTimePlayed: string;
    favoriteMuseName: string;
  };
}

interface DeviceHandoffEmailPayload {
  squadName: string;
  teacherName: string;
  teacherEmail: string;
  finishTime: string;
}

/**
 * A service to handle sending emails.
 * It works by creating a document in the `/mail` collection in Firestore,
 * which triggers a backend Cloud Function to do the actual sending.
 */
export class EmailService {
  /**
   * Triggers an email to a parent by writing a document to the 'mail' collection.
   * A backend Cloud Function will listen to this collection and send the actual email.
   *
   * @param payload - The data for the email.
   */
  static async triggerSongCreationEmail(payload: SongCreationEmailPayload): Promise<void> {
    const { parentEmail, studentName, song, userStats } = payload;

    const subject = `Look at what ${studentName} created on Overture!`;
    const htmlBody = this.buildHtml(studentName, song, userStats);

    try {
      await addDoc(collection(db, 'mail'), {
        to: parentEmail,
        subject: subject,
        html: htmlBody,
      });
      console.log('📧 Email request sent to backend successfully.');
    } catch (error) {
      console.error('🔥 Error sending email request to backend:', error);
    }
  }

  static async triggerDeviceHandoffEmail(payload: DeviceHandoffEmailPayload): Promise<void> {
    const { squadName, teacherName, teacherEmail, finishTime } = payload;
    const subject = `Device Handoff Location Needed for Squad: ${squadName}`;
    const htmlBody = `
      <div style="font-family: Arial, sans-serif; color: #333; line-height: 1.6;">
        <p>Hello,</p>
        <p>
          A teacher has indicated they do not know where to bring the devices after finishing a class.
        </p>
        <ul>
          <li><strong>Squad:</strong> ${squadName}</li>
          <li><strong>Teacher:</strong> ${teacherName} (${teacherEmail})</li>
          <li><strong>Class Finished At:</strong> ${new Date(finishTime).toLocaleString()}</li>
        </ul>
        <p>Please follow up to provide directions.</p>
      </div>
    `;

    try {
      await addDoc(collection(db, 'mail'), {
        to: 'steven@overtureai.org',
        subject: subject,
        html: htmlBody,
      });
      console.log('📧 Device handoff email request sent successfully.');
    } catch (error) {
      console.error('🔥 Error sending device handoff email request:', error);
    }
  }

  private static buildHtml(studentName: string, song: SavedSong, userStats: { totalSongs: number; totalTimePlayed: string; favoriteMuseName: string }): string {
    const html = `
      <div style="font-family: Arial, sans-serif; color: #333; line-height: 1.6;">
        <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 10px;">
          <h1 style="color: #4FC3F7; text-align: center;">Overture</h1>
          <h2 style="color: #a259e6;">Look at what ${studentName} created today!</h2>
          <p>Hi there,</p>
          <p>${studentName} just saved a new musical creation called <strong>"${song.title}"</strong> while playing on Overture. You can listen to it right now!</p>
          <div style="text-align: center; margin: 25px 0;">
            <a 
              href="${song.audioUrl}" 
              style="background-color: #4FC3F7; color: white; padding: 12px 25px; text-decoration: none; border-radius: 5px; font-weight: bold;"
            >
              ▶️ Listen to the Song
            </a>
          </div>
          <hr style="border: 0; border-top: 1px solid #eee; margin: 20px 0;" />
          <h3 style="color: #a259e6;">${studentName}'s Progress</h3>
          <ul>
            <li><strong>Total Songs Created:</strong> ${userStats.totalSongs}</li>
            <li><strong>Time Played:</strong> ${userStats.totalTimePlayed}</li>
            <li><strong>Favorite Muse:</strong> ${userStats.favoriteMuseName}</li>
          </ul>
          <p style="margin-top: 25px; font-size: 12px; color: #888;">
            Thank you for supporting your student's musical journey with Overture!
          </p>
        </div>
      </div>
    `;
    return html;
  }
} 
